import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdKeyboardArrowDown, MdCheck } from 'react-icons/md';

interface Props {
  options: string[];
  values: string[];
  onChange: (v: string[]) => void;
  label?: string;
  maxChips?: number;
}

const TagMultiSelect: React.FC<Props> = ({
  options,
  values,
  onChange,
  label = 'Tags',
  maxChips = 3,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((t) => t.toLowerCase().includes(q));
  }, [options, query]);

  // clic extérieur pour fermer
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (tag: string) => {
    if (values.includes(tag)) onChange(values.filter((t) => t !== tag));
    else onChange([...values, tag]);
  };
  const clear = () => onChange([]);
  const selectAllFiltered = () => {
    const set = new Set(values);
    filtered.forEach((t) => set.add(t));
    onChange(Array.from(set));
  };
  const removeFromSelection = (tag: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    onChange(values.filter((t) => t !== tag));
  };

  return (
    <div className="relative" ref={ref}>
      <div className="inline-flex items-center gap-2 h-8">
        <label className="text-[11px] leading-none text-gray-500 hidden sm:inline">{label}</label>
        <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-48 sm:w-60 max-w-[260px] rounded-md border border-gray-300 px-2 h-8 text-xs bg-white flex items-center justify-between gap-2 pr-1"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={values.length ? values.join(', ') : 'Tous les tags'}
      >
        <div className="flex items-center gap-1 overflow-hidden">
          {values.length === 0 && <span className="text-gray-500">Tous</span>}
          {values.slice(0, maxChips).map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-[2px] rounded bg-gray-100 text-gray-700"
              onClick={(e) => removeFromSelection(t, e)}
            >
              {t}
              <span className="text-gray-400 hover:text-gray-600 cursor-pointer">×</span>
            </span>
          ))}
          {values.length > maxChips && (
            <span className="text-[10px] px-2 py-[2px] rounded bg-gray-100 text-gray-700">
              +{values.length - maxChips}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {values.length > 0 && (
            <span
              className="text-gray-400 hover:text-gray-600 px-1"
              onClick={(e) => {
                e.stopPropagation();
                clear();
              }}
              title="Effacer"
            >
              ×
            </span>
          )}
          <MdKeyboardArrowDown className="text-gray-500" />
        </div>
      </button>
    </div>
      {open && (
        <div
          role="listbox"
          tabIndex={-1}
          className="absolute z-20 mt-1 w-[18rem] sm:w-[20rem] max-h-64 overflow-auto rounded-md border border-gray-200 bg-white shadow-3xl p-2"
        >
          <div className="flex items-center gap-2 mb-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un tag..."
              className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-brand-500 outline-none"
            />
            <button
              type="button"
              className="text-[11px] text-toola-600 hover:underline"
              onClick={selectAllFiltered}
              title="Ajouter les tags filtrés"
            >
              Tout
            </button>
            <button
              type="button"
              className="text-[11px] text-gray-600 hover:underline"
              onClick={clear}
              title="Effacer"
            >
              Effacer
            </button>
          </div>
          <ul className="space-y-1">
            {filtered.length === 0 && (
              <li className="text-[11px] text-gray-500 px-1 py-1">Aucun tag</li>
            )}
            {filtered.map((t) => {
              const checked = values.includes(t);
              return (
                <li
                  key={t}
                  className={`flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50 cursor-pointer ${
                    checked ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => toggle(t)}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-3 w-3 inline-flex items-center justify-center rounded-sm border ${
                        checked ? 'bg-toola-500 border-toola-500 text-white' : 'border-gray-300'
                      }`}
                    >
                      {checked && <MdCheck className="text-[10px]" />}
                    </span>
                    <span className="text-xs text-gray-700">{t}</span>
                  </div>
                  {checked && <span className="text-[10px] text-toola-600">sélectionné</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TagMultiSelect;